import React, { FC } from 'react';
import styled from 'styled-components';
import { connectDeep } from '../../../redux-utils';

import ContentGrid from '../AppContentGrid/ContentGrid';
import ItemGrid from '../AppContentGrid/ItemGrid';
import { ContentList, ItemRow } from '../AppContentList/index';
import AppShapedImage from '../AppShapedImage/index';
import App from '../../../models/app';
import { roundEdgeDevices, AppType } from '../../../constants';

const AppIcon = styled.img`
  width: 100%;
  max-width: 110px;
  height: auto;
  aspect-ratio: 1 / 1;
  border-radius: 16px;
  object-fit: contain;
  margin-bottom: 12px;
`;

const GridRowContainer = styled(ContentGrid)`
  padding-top: 0;
  padding-bottom: 0;
`;

const ListRowContainer = styled(ContentList)`
  padding-top: 0;
  padding-bottom: 0;
`;

const GridPlaceholderItem = styled.div`
  width: 100%;
`;

export const ITEMS_PER_ROW = 3;

interface AppRowProps {
  index: number;
  apps: App[];
  collectionId?: string;
  appType?: AppType | string;
  deviceName?: string;
}

const AppRowComponent: FC<AppRowProps> = ({
  index,
  apps,
  collectionId,
  appType = AppType.APP,
  deviceName = '',
}) => {
  const normalizedType = appType ? appType.toLowerCase() : AppType.APP;
  // Render Tiles as a list row
  if (normalizedType === AppType.TILE) {
    const app = apps[index];
    if (!app) return null;

    const queryParam = collectionId ? `?collectionId=${collectionId}` : '';
    const linkTo = `/v2/tile/${app.id}${queryParam}`;

    const photo = (
      <AppShapedImage
        photoProperties={{
          src: app.banners?.[0]?.uri || '',
          alt: app.name,
          width: app.banners?.[0]?.width,
          height: app.banners?.[0]?.height,
          isSquircle: roundEdgeDevices.includes(deviceName?.toLowerCase()),
        }}
        width={90}
        height={90}
        borderRadius="16px"
        borderReservedSpace={false}
      />
    );

    return (
      <ListRowContainer>
        <ItemRow
          id={app.id}
          title={app.name}
          linkTo={linkTo}
          photo={photo}
          subtitle={app.isInstalledOrInstalling ? 'installed' : undefined}
        />
      </ListRowContainer>
    );
  }

  // Render Clocks & Apps as 3-item grid row
  const startIndex = index * ITEMS_PER_ROW;
  const rowItems = [];

  for (let i = 0; i < ITEMS_PER_ROW; i++) {
    const itemIndex = startIndex + i;
    const app = apps[itemIndex];

    if (app) {
      const queryParam = collectionId ? `?collectionId=${collectionId}` : '';
      const isClock = normalizedType === AppType.CLOCK;
      const linkTo = isClock ? `/v2/clock/${app.id}${queryParam}` : `/v2/app/${app.id}${queryParam}`;

      const photo = isClock ? (
        <AppShapedImage
          photoProperties={{
            src: app.banners?.[0]?.uri || '',
            alt: app.name,
            width: app.banners?.[0]?.width,
            height: app.banners?.[0]?.height,
            isSquircle: roundEdgeDevices.includes(deviceName?.toLowerCase()),
          }}
          width={110}
          height={110}
          borderRadius="16px"
          borderReservedSpace={true}
        />
      ) : (
        <AppIcon src={app.icon?.uri || ''} alt={app.name} />
      );

      rowItems.push(
        <ItemGrid
          key={app.id}
          id={app.id}
          title={app.name}
          subtitle={app.isInstalledOrInstalling ? 'installed' : undefined}
          linkTo={linkTo}
          photo={photo}
        />
      );
    } else {
      rowItems.push(<GridPlaceholderItem key={`placeholder-${i}`} />);
    }
  }

  return <GridRowContainer>{rowItems}</GridRowContainer>;
};

const mapStateToProps = (state: any) => {
  const deviceName = state.registry.currentUser?.device?.productName || null;
  return {
    deviceName,
  };
};

export const AppRow = connectDeep(mapStateToProps)(AppRowComponent);
